import { HttpMethod } from '@/types/HttpMethods';
import axios, { AxiosResponse, AxiosRequestConfig } from 'axios';
import { TaskResponse } from './types';

/**
 * Человекочитаемый текст ошибки из ответа сервиса.
 *
 * ESB-коннекторы и Talend отвечают конвертом {statusCode, result, error}, где в
 * `error` уже лежит готовый текст для пользователя — например, гейт 409
 * «Лист уже отправлен в ERP, изменение данных невозможно». axios на любой не-2xx
 * бросает исключение, и раньше тело ответа здесь терялось целиком: наверх уходило
 * axios-овское `error.message`, поэтому вместо объяснения пользователь получал
 * «Request failed with status code 409».
 */
function readableError(error: any): string {
  const status = error.response?.status;
  const suffix = status ? ` (HTTP ${status})` : '';
  const data = error.response?.data;
  if (typeof data === 'string' && data.trim() !== '') return data.trim() + suffix;
  if (data && typeof data === 'object') {
    // Конверт ESB-джобов — {status, body, error}: при отказе kie-server описание
    // приходит в `body`, а `error` остаётся null. Раньше эта ветка проваливалась
    // в axios-овское «Request failed with status code 500», и причина терялась.
    const text = data.error ?? data.message ?? data.body;
    if (typeof text === 'string' && text.trim() !== '') return text.trim() + suffix;
    try {
      const dump = JSON.stringify(data);
      if (dump && dump !== '{}' && dump !== 'null') return dump.slice(0, 500) + suffix;
    } catch {
      // тело не сериализуется — ниже отдадим сообщение axios
    }
  }
  return error.message || String(error);
}

class Task {
  public static async execute(
    {
      url,
      method = HttpMethod.GET,
      body,
      headers,
    }: { url: string; method: HttpMethod; body?: any; headers?: any },
  ): Promise<TaskResponse> {
    try {
      let response: AxiosResponse<any> = {
        data: {},
        status: 0,
        statusText: '',
        headers: '',
        config: {},
      };

      switch (method) {
        case HttpMethod.GET:
          response = await axios.get(url, { headers });
          break;
        case HttpMethod.POST:
          response = await axios.post(url, body, { headers });
          break;
        case HttpMethod.PUT:
          response = await axios.put(url, body, { headers });
          break;
        case HttpMethod.DELETE:
          response = await axios.delete(url, { headers });
          break;
        default:
          throw new Error(`Unrecognized http method - ${method}`);
      }

      const { data, status } = response;

      return {
        result: [null, data],
        code: status,
      };
    } catch (error) {
      console.error(error);
      return {
        code: error.response?.status ?? 500,
        result: [readableError(error), null],
      };
    }
  }
}

export default Task;
